import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, Alert, Pressable, StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Observables from '@happy-gastro/global-observables';
import Icon from '../components/management/SafeIcon';
import {useNavigation} from '@react-navigation/native';
import {useTheme} from '../helpers/ThemeContext';
import {useManagementTheme} from '../helpers/managementTheme';
import {MANAGEMENT_REFRESH_PRESETS, getManagementScope} from '../helpers/managementApi';
import {useManagementData} from '../helpers/ManagementDataContext';
import {getUserInfo} from '../helpers/storage';
import {getSetting, setSetting} from '../helpers/persistentSettings';
import {useMail} from '../helpers/MailContext';
import {apiFetch} from '../helpers/WorkSheetContext';
import {setUserInfo} from '../helpers/storage';
import {persistRestaurant} from '../helpers/restaurantCache';
import {safeSetJson} from '../helpers/storageQuota';
import {collectUserRoles, getAvailableAppModesForUser, getDefaultOperationalModeForUser, isSystemAdmin} from '../helpers/managerRoles';
import {useManagementAccess} from '../helpers/managementAccess';
import {Card, DetailColumns, SectionTitle} from '../components/management/ManagementUI';
import {SearchField} from '../components/management/ManagementHubKit';
import {IconChip, RoundIconButton} from '../components/premium/PremiumKit';
import Avatar from '../components/Avatar';
import DemoModeToggleButton from '../components/DemoModeToggleButton';
import {PremiumSwitch} from '../components/settings/SettingsUiKit';
import {setPosPreference, usePosPreference} from '../helpers/posPreferences';
import {resolveUserNames} from '../helpers/userNames';
import ManagementScreen from '../components/management/ManagementScreen';
import {filterModesForAppVariant, resolveModeForAppVariant} from '../config/appVariant';
import {applyHomeLanding, getCurrentHomeLandingId, getHomeLandingOptions} from '../helpers/homeLanding';
import i18n from '../../i18n';

const THEME_OPTIONS = [
    {mode: 'light', labelKey: 'mgmt_settings_theme_light', icon: 'white-balance-sunny'},
    {mode: 'dark', labelKey: 'mgmt_settings_theme_dark', icon: 'weather-night'},
    {mode: 'system', labelKey: 'mgmt_settings_theme_system', icon: 'cellphone-cog'},
];

const MODULE_OPTIONS = {
    // `restaurant` is the POS till; the `sales` entry below is the NEW CRM-driven
    // field-sales app. Both read their label from the shared `module_switch_*`
    // keys so this screen never drifts from the POS settings picker.
    restaurant: {labelKey: 'module_switch_pos', label: 'POS app', targetRoute: 'HomeScreen', tone: 'success'},
    sales: {labelKey: 'module_switch_sales', label: 'Sales app', targetRoute: 'SalesHomeScreen', tone: 'primary'},
    service: {labelKey: 'mgmt_settings_module_service_app', label: 'Service app', targetRoute: 'RepairmanHomeScreen', tone: 'info'},
    stock: {labelKey: 'mgmt_settings_module_logistics_app', label: 'Logisztikai app (Kiszedő app)', targetRoute: 'WarehousemanHomeScreen', tone: 'warning'},
    driver: {labelKey: 'module_switch_driver', label: 'Sofőr / Futár app', targetRoute: 'DriverHomeScreen', tone: 'danger'},
    manager: {labelKey: 'mgmt_settings_module_management_app', label: 'Management app', targetRoute: 'ManagementHomeScreen', tone: 'violet'},
};

/** Resolve a MODULE_OPTIONS entry's localized label. */
const moduleOptionLabel = (cfg) => (cfg?.labelKey ? i18n.t(cfg.labelKey, {defaultValue: cfg.label}) : (cfg?.label || ''));

const ALL_PLACES_SCOPE = {
    _id: '__all__',
    name: i18n.t('management_scope_all_places'),
    raw: 'All places',
    managerAllPlaces: true,
};

const PLACE_TYPE_LABELS = {
    restaurant: 'place_type_restaurant',
    bar: 'place_type_bar',
    cafe: 'place_type_cafe',
    hotel: 'place_type_hotel',
    shop: 'place_type_shop',
    bakery: 'place_type_bakery',
    foodtruck: 'place_type_foodtruck',
    canteen: 'place_type_canteen',
    fastfood: 'place_type_fastfood',
};

const formatPlaceType = (type) => {
    const key = PLACE_TYPE_LABELS[String(type || '').toLowerCase()];
    return key ? i18n.t(key) : String(type || '');
};

/**
 * Coerce an arbitrary value (string, structured-address object, or accidental
 * function reference) into clean display text. Guards against rendering
 * "function String() { [native code] }" or "[object Object]" when a non-string
 * value slips through (see QA MGMT-023).
 * @param {*} value the raw value.
 * @returns {string}
 */
const cleanText = (value) => {
    if (value == null || typeof value === 'function') return '';
    if (typeof value === 'object') {
        const parts = [
            value.zipCode || value.postCode || value.zip,
            value.city,
            value.address || value.street || value.raw,
            value.houseNumber,
        ].filter((p) => p && typeof p === 'string');
        return parts.join(' ').trim();
    }
    const s = String(value).trim();
    if (s === '[object Object]' || /\[native code\]/.test(s)) return '';
    return s;
};

/**
 * Best-effort phone number formatting for the partner list. Normalizes Hungarian
 * numbers to "+36 XX XXX XXXX" and leaves clearly malformed/short values as-is
 * rather than mangling them (see QA MGMT-025).
 * @param {*} raw the raw phone value.
 * @returns {string}
 */
const formatPhone = (raw) => {
    const s = cleanText(raw);
    if (!s) return '';
    const compact = s.replace(/[^\d+]/g, '');
    const digits = compact.replace(/\D/g, '');
    if (digits.length < 7) return s; // too short to be a real number — keep as typed
    let d = digits;
    if (d.startsWith('06')) d = `36${d.slice(2)}`;
    else if (d.startsWith('0036')) d = d.slice(2);
    if (d.startsWith('36') && d.length >= 10) {
        const rest = d.slice(2);
        return `+36 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`.trim();
    }
    return compact.startsWith('+') ? compact : `+${d}`;
};

const getInitials = (value) => {
    const words = String(value || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (words.length === 0) {
        return '–';
    }

    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }

    return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

/**
 * Role precedence for the profile card: the first role of this list that the
 * user holds is shown as their headline role.
 * @type {Array<{role: string, key: string, fallback: string}>}
 */
const PROFILE_ROLES = [
    {role: 'system:admin', key: 'role_system_admin', fallback: 'Rendszergazda'},
    {role: 'sales:admin', key: 'role_sales_admin', fallback: 'Értékesítési admin'},
    {role: 'restaurantStaff:admin', key: 'role_admin', fallback: 'Admin'},
    {role: 'restaurantStaff:manager', key: 'role_manager', fallback: 'Menedzser'},
    {role: 'sales:salesman', key: 'role_salesman', fallback: 'Értékesítő'},
    {role: 'serviceTechnician', key: 'role_technician', fallback: 'Szerviz technikus'},
    {role: 'restaurantStaff:cashier', key: 'role_cashier', fallback: 'Pénztáros'},
    {role: 'restaurantStaff:waiter', key: 'role_waiter', fallback: 'Pincér'},
    {role: 'restaurantStaff:delivery', key: 'role_delivery', fallback: 'Futár'},
];

/**
 * Localized headline role of a user for the profile card.
 * @param {?object} user Stored user record.
 * @returns {string} Role label, or '' when the user holds no recognisable role.
 */
const primaryRoleLabel = (user) => {
    if (!user) {
        return '';
    }

    const roles = collectUserRoles(user, null);
    const hit = PROFILE_ROLES.find((entry) => roles.includes(entry.role));

    if (hit) {
        return i18n.t(hit.key, {defaultValue: hit.fallback});
    }

    return roles[0] ? String(roles[0]).split(':').pop() : '';
};

/**
 * Pill option (theme / refresh interval / home screen). Active = solid orange
 * with white text; inactive = soft grey fill.
 *
 * @param {object} props
 * @param {string} props.label Pill text.
 * @param {string} [props.icon] Leading MDI glyph.
 * @param {boolean} props.active Selected state.
 * @param {Function} props.onPress Tap handler.
 * @param {boolean} [props.grow=false] Share the row width equally with sibling pills.
 * @param {boolean} [props.stacked=false] Glyph above the label (compact equal-width choices such as the theme picker).
 * @returns {JSX.Element} The pill.
 */
const OptionPill = ({label, icon, active, onPress, grow = false, stacked = false}) => {
    const m = useManagementTheme();
    const fg = active ? m.colors.onPrimary : m.colors.subText;

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityState={{selected: active}}
            style={({pressed}) => [
                styles.pill,
                grow && styles.pillGrow,
                stacked && styles.pillStacked,
                {backgroundColor: active ? m.colors.primary : m.colors.surfaceAlt, opacity: pressed ? 0.85 : 1},
            ]}
        >
            {!!icon && <Icon name={icon} size={17} color={fg} />}
            <Text style={[styles.pillText, {color: fg}]} numberOfLines={1}>{label}</Text>
        </Pressable>
    );
};

/**
 * One row of a grouped settings card: tinted icon chip, title, optional
 * subtitle, chevron. A locked row shows its lock chip + reason and never fires.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {string} props.title Row title.
 * @param {string} [props.subtitle] Muted description.
 * @param {Function} [props.onPress] Tap handler.
 * @param {boolean} [props.disabled=false] Locked state.
 * @param {string} [props.lockedReason] Why the row is locked.
 * @param {React.ReactNode} [props.right] Replaces the trailing chevron.
 * @param {boolean} [props.last=false] Omit the divider under the row.
 * @returns {JSX.Element} The row.
 */
const SettingsRow = ({icon, tone = 'primary', title, subtitle, onPress, disabled = false, lockedReason, right, last = false}) => {
    const m = useManagementTheme();

    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            disabled={disabled || !onPress}
            accessibilityRole="button"
            accessibilityState={{disabled}}
            style={({pressed}) => [
                styles.row,
                !last && {borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: m.colors.border},
                {backgroundColor: pressed ? m.colors.surfaceAlt : 'transparent', opacity: disabled ? 0.55 : 1},
            ]}
        >
            <IconChip icon={disabled ? 'lock-outline' : icon} tone={disabled ? 'neutral' : tone} size={42} />
            <View style={styles.rowText}>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                {!!subtitle && !disabled && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
                {disabled && !!lockedReason && <Text style={[styles.rowSub, {color: m.colors.muted}]} numberOfLines={2}>{lockedReason}</Text>}
            </View>
            {right || <Icon name={disabled ? 'lock-outline' : 'chevron-right'} size={20} color={m.colors.muted} />}
        </Pressable>
    );
};

/**
 * Head of a settings block that carries its own controls (appearance, refresh,
 * home screen): tinted chip + bold title.
 *
 * @param {{icon: string, tone?: string, title: string}} props
 * @returns {JSX.Element} The block heading.
 */
const BlockHead = ({icon, tone = 'primary', title}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.blockHead}>
            <IconChip icon={icon} tone={tone} size={36} round />
            <Text style={[styles.blockTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
        </View>
    );
};

/**
 * Management settings hub — profile card (avatar, name, role, store), grouped
 * setting rows in white cards with tinted icon chips, pill options and the
 * developer tools for system admins. Every handler / persisted preference is
 * the one the screen always had; only the presentation changed.
 *
 * @returns {JSX.Element} The settings hub.
 */
export default function ManagementSettingsScreen() {
    const { t } = useTranslation();
    const hapticsEnabled = usePosPreference('hapticsEnabled');
    const navigation = useNavigation();
    const m = useManagementTheme();
    const {themeMode, setThemeMode} = useTheme();
    const {canAccess} = useManagementAccess();
    const {configured: mailConfigured} = useMail();
    const {intervalMs, setIntervalMs} = useManagementData();
    const [scope, setScope] = useState(null);
    // "Main screen after login" preference — the active landing id plus the mode
    // and place used to build the picker (see helpers/homeLanding).
    const [activePlace, setActivePlace] = useState(null);
    const [currentMode, setCurrentMode] = useState('manager');
    const [currentLandingId, setCurrentLandingId] = useState('manager');
    const [moduleHint, setModuleHint] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [restaurantsLoading, setRestaurantsLoading] = useState(false);
    const [restaurantSearch, setRestaurantSearch] = useState('');
    const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
    const [switchingRestaurant, setSwitchingRestaurant] = useState(false);
    const [partnerContextOpen, setPartnerContextOpen] = useState(false);

    const isAdmin = useMemo(() => {
        const roles = collectUserRoles(currentUser, null);
        const defaultRoles = Array.isArray(currentUser?.defaultRoles) ? currentUser.defaultRoles : [];
        const effectiveRoles = Array.isArray(currentUser?.effectiveRoles) ? currentUser.effectiveRoles : [];
        const allRoles = [...roles, ...defaultRoles, ...effectiveRoles].map((role) => String(role || ''));

        // Partner switch ("Belépés mint") is available to system:admin and sales:admin.
        return allRoles.includes('system:admin') || allRoles.includes('sales:admin');
    }, [currentUser]);

    const filteredRestaurants = useMemo(() => {
        const query = String(restaurantSearch || '').trim().toLowerCase();

        if (!query) {
            return restaurants;
        }

        return restaurants.filter((item) => {
            const name = String(item?.name || '').toLowerCase();
            const billingName = String(item?.billingName || '').toLowerCase();
            const vatNo = String(item?.vatNo || '').toLowerCase();

            return name.includes(query) || billingName.includes(query) || vatNo.includes(query);
        });
    }, [restaurantSearch, restaurants]);

    const selectedRestaurant = useMemo(
        () => restaurants.find((item) => String(item?._id || item?.id || '') === String(selectedRestaurantId || '')) || null,
        [restaurants, selectedRestaurantId],
    );

    const normalizeRestaurantRecord = useCallback((item) => {
        if (!item || typeof item !== 'object') {
            return null;
        }

        const source = item.restaurant && typeof item.restaurant === 'object' ? item.restaurant : item;
        const id = String(source?._id || source?.id || source?.restaurantId || '').trim();
        const name = String(
            source?.name
            || source?.nev
            || source?.company
            || source?.title
            || source?.displayName
            || ''
        ).trim();

        // Drop unidentifiable or unnamed records (e.g. raw hash-only entries).
        if (!id || !name) {
            return null;
        }

        const places = Array.isArray(source?.places) ? source.places : [];
        const firstPlace = places.find((place) => place && typeof place === 'object') || null;
        const contact = firstPlace?.contact && typeof firstPlace.contact === 'object' ? firstPlace.contact : {};
        const placeTypes = Array.isArray(source?.placeType) ? source.placeType.filter(Boolean) : [];
        const openCount = places.filter((place) => place?.isOpen).length;

        return {
            ...source,
            _id: id,
            id,
            name,
            billingName: String(source?.billingName || source?.billing_name || '').trim(),
            vatNo: String(source?.vatNo || source?.vat_no || source?.adoszam || '').trim(),
            placesCount: places.length,
            openCount,
            placeTypes,
            address: cleanText(firstPlace?.raw || firstPlace?.name || firstPlace?.address || source?.address),
            phone: formatPhone(contact?.phone || source?.contact?.phone),
            email: cleanText(contact?.email || source?.contact?.email),
            isAnyOpen: openCount > 0,
        };
    }, []);

    const normalizeRestaurantList = useCallback((response) => {
        const candidates = [
            response,
            response?.obj,
            response?.restaurants,
            response?.data,
            response?.data?.restaurants,
            response?.body,
            response?.result,
            response?.result?.restaurants,
            response?.items,
            response?.docs,
        ];

        const firstArray = candidates.find((entry) => Array.isArray(entry));

        return Array.isArray(firstArray) ? firstArray : [];
    }, []);

    // "Main screen after login" destinations — every entitled module plus the
    // Management sub-screens (Calendar, Mailbox, …). Shared with the POS settings
    // screen via helpers/homeLanding so both pickers stay in sync.
    const landingOptions = useMemo(
        () => getHomeLandingOptions({user: currentUser, place: activePlace, canAccess, mailConfigured, currentMode}),
        [currentUser, activePlace, canAccess, mailConfigured, currentMode],
    );
    const currentLandingOptions = useMemo(() => landingOptions.filter((option) => option.sameMode), [landingOptions]);
    const switchLandingOptions = useMemo(() => landingOptions.filter((option) => !option.sameMode), [landingOptions]);

    const selectLanding = useCallback(async (option) => {
        if (!option) return;
        // Same-module choices only pin the tab (apply on the module's next open);
        // picking a different app switches now and reloads (see applyHomeLanding).
        setCurrentLandingId(option.id);
        await applyHomeLanding(option);
    }, []);

    const loadScope = useCallback(async () => {
        setScope(await getManagementScope());
    }, []);

    const loadModuleSettings = useCallback(async () => {
        const [storedMode, user, placeRaw] = await Promise.all([
            getSetting('userAppMode'),
            getUserInfo(),
            AsyncStorage.getItem('restaurantPlace'),
        ]);

        const place = placeRaw ? JSON.parse(placeRaw) : null;
        const defaultMode = getDefaultOperationalModeForUser(user, place);
        const nextModules = filterModesForAppVariant(getAvailableAppModesForUser(user, place));

        const resolvedMode = resolveModeForAppVariant(nextModules.includes(storedMode) ? storedMode : defaultMode, defaultMode);
        if (!nextModules.includes(storedMode)) {
            await setSetting('userAppMode', resolvedMode);
        }

        setCurrentUser(user || null);
        setActivePlace(place);
        setCurrentMode(resolvedMode);
        setCurrentLandingId(await getCurrentHomeLandingId());
        const hintMode = nextModules.includes(defaultMode) ? defaultMode : resolvedMode;
        setModuleHint(t('mgmt_settings_default_module_hint', {module: moduleOptionLabel(MODULE_OPTIONS[hintMode]) || moduleOptionLabel(MODULE_OPTIONS.restaurant)}));
    }, []);

    const loadRestaurants = useCallback(async () => {
        if (!isAdmin) {
            setRestaurants([]);
            return;
        }

        setRestaurantsLoading(true);
        try {
            const [response, activeRestaurantRaw, user] = await Promise.all([
                apiFetch('/restaurants/list').catch(() => null),
                AsyncStorage.getItem('restaurant'),
                getUserInfo(),
            ]);
            const userRestaurants = Array.isArray(user?.restaurants) ? user.restaurants : [];
            const activeRestaurant = activeRestaurantRaw ? JSON.parse(activeRestaurantRaw) : null;
            const apiRestaurants = normalizeRestaurantList(response);

            const mergedById = new Map();
            [...userRestaurants, activeRestaurant, ...apiRestaurants]
                .map((item) => normalizeRestaurantRecord(item))
                .filter(Boolean)
                .forEach((item) => {
                    mergedById.set(String(item._id), item);
                });

            const list = Array.from(mergedById.values())
                .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'hu'));

            // Selection priority:
            //  1. exactly one restaurant -> always pick it as the default,
            //  2. the currently active restaurant context,
            //  3. the admin's first own restaurant,
            //  4. the first item of the merged list.
            const firstUserRestaurant = normalizeRestaurantRecord(userRestaurants[0]);
            const activeId = list.length === 1
                ? String(list[0]?._id || list[0]?.id || '')
                : String(
                    activeRestaurant?._id || activeRestaurant?.id
                    || firstUserRestaurant?._id || firstUserRestaurant?.id
                    || list[0]?._id || list[0]?.id || ''
                );

            setRestaurants(list);
            setSelectedRestaurantId(activeId);
        } catch (_error) {
            setRestaurants([]);
            Alert.alert(i18n.t('error'), i18n.t('restaurant_list_load_failed'));
        } finally {
            setRestaurantsLoading(false);
        }
    }, [isAdmin, normalizeRestaurantList, normalizeRestaurantRecord]);

    useEffect(() => {
        if (!selectedRestaurantId && filteredRestaurants.length > 0) {
            setSelectedRestaurantId(String(filteredRestaurants[0]?._id || filteredRestaurants[0]?.id || ''));
            return;
        }

        const selectedVisible = filteredRestaurants.some(
            (item) => String(item?._id || item?.id || '') === String(selectedRestaurantId || ''),
        );

        if (!selectedVisible && filteredRestaurants.length === 1) {
            setSelectedRestaurantId(String(filteredRestaurants[0]?._id || filteredRestaurants[0]?.id || ''));
        }
    }, [filteredRestaurants, selectedRestaurantId]);

    const applyUserContext = useCallback(async (nextUser, nextRestaurant) => {
        if (!nextUser || !nextRestaurant) return;

        await setUserInfo(nextUser);
        // Slim projection + quota-guarded write (helpers/restaurantCache): the raw
        // store document carries unbounded credit ledgers that crossed the web
        // storage quota, and switching partner stores rewrites this key every time.
        await persistRestaurant(nextRestaurant);
        await safeSetJson('restaurantPlace', ALL_PLACES_SCOPE, { label: 'restaurantPlace' });
        await setSetting('userAppMode', 'manager');
    }, []);

    const restartThroughStartupFlow = useCallback(() => {
        // Full root remount (index.js `Root` → remounts <App/>), NOT a plain
        // navigation.reset(). Switching the managed partner rewrites the persisted
        // `restaurant`/`restaurantPlace`, but the live ManagementDataProvider is
        // mounted ABOVE the navigator and caches its scope for the lifetime of the
        // mount — a navigation.reset() leaves that provider (and App.jsx's
        // `currentRestaurant`) untouched, so the dashboard keeps serving the OLD
        // restaurant. `restartApp` replays the cold-boot path: App.jsx
        // loadRuntimeState() re-reads the new restaurant + `userAppMode`, and the
        // provider remounts with a fresh scope, so the newly-selected partner's
        // data actually loads. React remount (not an OS restart) → identical on
        // iOS, Android, Electron and web.
        Observables.call('restartApp');
    }, []);

    const handleLoginAsRestaurant = useCallback(async () => {
        if (!selectedRestaurant || switchingRestaurant) return;

        setSwitchingRestaurant(true);
        try {
            const user = await getUserInfo();
            const currentRoles = Array.isArray(user?.roles) ? user.roles : [];
            const baseRoles = Array.isArray(user?.defaultRoles) && user.defaultRoles.length > 0
                ? user.defaultRoles
                : currentRoles;
            const nextUser = {
                ...user,
                defaultRoles: baseRoles,
                roles: ['restaurantStaff:admin'],
                restaurants: [selectedRestaurant],
                showRestaurants: false,
            };

            await applyUserContext(nextUser, selectedRestaurant);
            setCurrentUser(nextUser);
            await loadScope();
            restartThroughStartupFlow();
        } catch (_error) {
            Alert.alert(i18n.t('error'), i18n.t('partner_context_switch_failed'));
        } finally {
            setSwitchingRestaurant(false);
        }
    }, [applyUserContext, loadScope, restartThroughStartupFlow, selectedRestaurant, switchingRestaurant]);

    const handleRestoreDefaultContext = useCallback(async () => {
        if (switchingRestaurant) return;

        setSwitchingRestaurant(true);
        try {
            const lastUserRaw = await AsyncStorage.getItem('lastUser');
            const lastUser = lastUserRaw ? JSON.parse(lastUserRaw) : null;
            const fallbackRestaurant = Array.isArray(lastUser?.restaurants) ? lastUser.restaurants[0] : null;

            if (!lastUser || !fallbackRestaurant) {
                Alert.alert(i18n.t('missing_data_title'), i18n.t('no_default_partner_to_restore'));
                return;
            }

            await applyUserContext(lastUser, fallbackRestaurant);
            setCurrentUser(lastUser);
            setSelectedRestaurantId(String(fallbackRestaurant?._id || fallbackRestaurant?.id || ''));
            await loadScope();
            restartThroughStartupFlow();
        } catch (_error) {
            Alert.alert(i18n.t('error'), i18n.t('default_partner_restore_failed'));
        } finally {
            setSwitchingRestaurant(false);
        }
    }, [applyUserContext, loadScope, restartThroughStartupFlow, switchingRestaurant]);

    useEffect(() => {
        loadScope();
        loadModuleSettings();
    }, [loadModuleSettings, loadScope]);

    useEffect(() => {
        loadRestaurants();
    }, [loadRestaurants]);

    const profileName = resolveUserNames(currentUser).full;
    const profileRole = primaryRoleLabel(currentUser);
    const storeName = scope?.restaurant?.name || t('mgmt_settings_restaurant_fallback');
    const placeName = scope?.placeLabel || t('mgmt_settings_place_fallback');

    /** Pill row for the "main screen" picker (current view / other apps). */
    const renderLandingPills = (options) => (
        <View style={styles.pillRow}>
            {options.map((option) => (
                <OptionPill
                    key={option.id}
                    icon={option.icon}
                    label={t(option.labelKey) || option.fallback}
                    active={currentLandingId === option.id}
                    onPress={() => selectLanding(option)}
                />
            ))}
        </View>
    );

    const navRows = [
        {
            key: 'store',
            icon: 'storefront-outline',
            tone: 'info',
            title: t('mgmt_settings_store_card_title'),
            subtitle: t('mgmt_settings_store_card_sub'),
            onPress: () => navigation.navigate('ManagementStoreSettingsScreen'),
        },
        {
            key: 'billing',
            icon: 'credit-card-outline',
            tone: 'success',
            title: i18n.t('billing_and_payments'),
            subtitle: i18n.t('billing_menu_subtitle'),
            onPress: () => navigation.navigate('ManagementBillingScreen'),
        },
        {
            key: 'users',
            icon: 'account-group-outline',
            tone: 'violet',
            title: t('mgmt_settings_users_card_title'),
            subtitle: t('mgmt_settings_users_card_sub'),
            onPress: () => navigation.navigate('ManagementUsersScreen'),
        },
        {
            key: 'roles',
            icon: 'shield-key-outline',
            tone: 'info',
            title: t('mgmt_settings_roles_card_title', {defaultValue: 'Custom roles'}),
            subtitle: t('mgmt_settings_roles_card_sub', {defaultValue: 'Menus, pages & functions per role + AI setup'}),
            onPress: () => navigation.navigate('ManagementRolesScreen'),
        },
        {
            key: 'products',
            icon: 'food-variant',
            tone: 'success',
            title: t('mgmt_settings_products_card_title'),
            subtitle: t('mgmt_settings_products_card_sub'),
            onPress: () => navigation.navigate('ManagementListScreen', {type: 'products'}),
        },
        {
            key: 'costTypes',
            icon: 'shape-plus-outline',
            tone: 'warning',
            title: t('mgmt_settings_costtypes_card_title'),
            subtitle: t('mgmt_settings_costtypes_card_sub'),
            onPress: () => navigation.navigate('ManagementListScreen', {type: 'costTypes'}),
        },
    ];

    return (
        <ManagementScreen safeTop>
            {/* Profile card — who is signed in, in which store; the cog opens the app functions screen. */}
            <View style={[styles.profile, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                <View style={styles.profileTop}>
                    <View style={[styles.avatarRing, {borderColor: m.colors.primary}]}>
                        <Avatar user={currentUser} size={64} />
                    </View>
                    <View style={styles.profileText}>
                        <Text style={[styles.profileEyebrow, {color: m.colors.primary}]} numberOfLines={1}>{t('mgmt_settings_hero_eyebrow')}</Text>
                        <Text style={[styles.profileName, {color: m.colors.heading}]} numberOfLines={2}>{profileName || t('mgmt_settings_hero_title')}</Text>
                        {!!profileRole && <Text style={[styles.profileRole, {color: m.colors.subText}]} numberOfLines={1}>{profileRole}</Text>}
                    </View>
                    <View style={styles.profileActions}>
                        <DemoModeToggleButton color={m.colors.subText} />
                        <RoundIconButton
                            icon="cog-outline"
                            size={42}
                            accessibilityLabel={t('mgmt_settings_hero_title')}
                            onPress={() => navigation.navigate('FunctionsScreen')}
                        />
                    </View>
                </View>
                <View style={styles.profileChips}>
                    <View style={[styles.scopePill, {backgroundColor: m.colors.chipBg}]}>
                        <Icon name="storefront-outline" size={14} color={m.colors.primary} />
                        <Text style={[styles.scopePillText, {color: m.colors.chipText}]} numberOfLines={1}>{storeName}</Text>
                    </View>
                    <View style={[styles.scopePill, {backgroundColor: m.colors.surfaceAlt}]}>
                        <Icon name={scope?.allPlaces ? 'storefront-outline' : 'store-marker-outline'} size={14} color={m.colors.subText} />
                        <Text style={[styles.scopePillText, {color: m.colors.subText}]} numberOfLines={1}>{placeName}</Text>
                    </View>
                </View>
            </View>

            {/* Two-up on tablets so these independent config cards read without
                endless scrolling; single column (pass-through) on phones. */}
            <DetailColumns>
                <View>
                    <SectionTitle title={t('mgmt_settings_appearance_section')} icon="palette-outline" tone="primary" eyebrow />
                    <Card>
                        <Text style={[styles.cardHint, {color: m.colors.subText}]}>{t('mgmt_settings_appearance_hint')}</Text>
                        <View style={styles.pillRow}>
                            {THEME_OPTIONS.map((option) => (
                                <OptionPill
                                    key={option.mode}
                                    grow
                                    stacked
                                    icon={option.icon}
                                    label={t(option.labelKey)}
                                    active={themeMode === option.mode}
                                    onPress={() => setThemeMode(option.mode)}
                                />
                            ))}
                        </View>

                        <View style={styles.refreshBlock}>
                            <BlockHead icon="autorenew" tone="info" title={t('mgmt_settings_refresh_section')} />
                            <View style={styles.pillRow}>
                                {MANAGEMENT_REFRESH_PRESETS.map((preset) => (
                                    <OptionPill
                                        key={preset.labelKey}
                                        label={t(preset.labelKey)}
                                        active={intervalMs === preset.value}
                                        onPress={() => setIntervalMs(preset.value)}
                                    />
                                ))}
                            </View>
                            <Text style={[styles.moduleHint, {color: m.colors.subText}]}>{t('mgmt_settings_refresh_hint')}</Text>
                        </View>
                    </Card>
                </View>

                <View>
                    <SectionTitle title={t('mgmt_settings_home_section')} icon="home-outline" tone="success" eyebrow />
                    <Card>
                        <Text style={[styles.cardHint, {color: m.colors.subText}]}>{t('home_landing_hint', {defaultValue: t('mgmt_settings_home_hint')})}</Text>

                        <Text style={[styles.landingGroupLabel, {color: m.colors.heading}]}>{t('home_landing_group_current', {defaultValue: 'Kezdőképernyő ebben a nézetben'})}</Text>
                        {renderLandingPills(currentLandingOptions)}
                        <Text style={[styles.moduleHint, {color: m.colors.subText}]}>{t('mgmt_settings_home_applies_next_open')}</Text>

                        {switchLandingOptions.length > 0 && (
                            <View style={styles.landingSwitchBlock}>
                                <Text style={[styles.landingGroupLabel, {color: m.colors.heading}]}>{t('home_landing_group_switch', {defaultValue: 'Váltás másik alkalmazásra'})}</Text>
                                {renderLandingPills(switchLandingOptions)}
                                <Text style={[styles.moduleHint, {color: m.colors.subText}]}>{t('home_landing_switch_note', {defaultValue: 'Másik app kiválasztása azonnal átvált és újraindítja az alkalmazást.'})}</Text>
                            </View>
                        )}

                        {!!moduleHint && <Text style={[styles.moduleHint, {color: m.colors.subText}]}>{moduleHint}</Text>}
                    </Card>
                </View>

                <View>
                    <SectionTitle title={t('mgmt_settings_notifications_section')} icon="bell-cog-outline" tone="warning" eyebrow />
                    <Card padded={false} style={styles.groupCard}>
                        <SettingsRow
                            icon="bell-cog-outline"
                            tone="warning"
                            title={t('notification_preferences')}
                            subtitle={t('notification_preferences_sub')}
                            onPress={() => navigation.navigate('NotificationSettingsScreen')}
                        />
                        <SettingsRow
                            icon="vibrate"
                            tone="violet"
                            title={t('pos_pref_haptics', 'Haptic feedback')}
                            subtitle={t('pos_pref_haptics_sub', 'A short vibration on taps, swipes and results in every module.')}
                            right={<PremiumSwitch value={hapticsEnabled} onValueChange={(next) => setPosPreference('hapticsEnabled', next)} />}
                            last
                        />
                    </Card>
                </View>
            </DetailColumns>

            <SectionTitle title={t('mgmt_settings_masterdata_section')} icon="database-cog-outline" tone="violet" eyebrow />
            <Card padded={false} style={styles.groupCard}>
                <SettingsRow
                    icon="account-switch-outline"
                    tone="violet"
                    title={t('mgmt_settings_partner_context')}
                    onPress={() => setPartnerContextOpen((prev) => !prev)}
                    right={<Icon name={partnerContextOpen ? 'chevron-up' : 'chevron-down'} size={22} color={m.colors.subText} />}
                />
                {partnerContextOpen && (
                    <View style={[styles.partnerBody, {borderBottomColor: m.colors.border}]}>
                        <Text style={[styles.cardHint, {color: m.colors.subText}]}>{t('mgmt_settings_partner_context_hint')}</Text>
                        {!isAdmin && (
                            <Text style={[styles.cardHint, {color: m.colors.subText}]}>
                                {t('mgmt_settings_partner_admin_required')}
                            </Text>
                        )}
                        <SearchField
                            inset
                            value={restaurantSearch}
                            onChangeText={setRestaurantSearch}
                            editable={isAdmin && !switchingRestaurant}
                            placeholder={t('mgmt_settings_partner_search_placeholder')}
                            clearLabel={t('mgmt_data_search_clear', {defaultValue: 'Keresés törlése'})}
                            style={{opacity: isAdmin ? 1 : 0.5}}
                        />

                        {!isAdmin ? (
                            <Text style={[styles.emptyListText, {color: m.colors.subText}]}>{t('mgmt_settings_partner_admin_only')}</Text>
                        ) : restaurantsLoading ? (
                            <View style={styles.loaderRow}>
                                <ActivityIndicator color={m.colors.primary} size="small" />
                                <Text style={[styles.loaderText, {color: m.colors.subText}]}>{t('mgmt_settings_restaurants_loading')}</Text>
                            </View>
                        ) : (
                            <View style={styles.restaurantList}>
                                <Text style={[styles.listCounter, {color: m.colors.subText}]}>{t('mgmt_settings_results_count', {count: filteredRestaurants.length})}</Text>
                                {filteredRestaurants.length === 0 ? (
                                    <Text style={[styles.emptyListText, {color: m.colors.subText}]}>{t('mgmt_settings_no_results')}</Text>
                                ) : null}
                                {filteredRestaurants.slice(0, 15).map((item) => {
                                    const itemId = String(item?._id || item?.id || '');
                                    const selected = itemId === String(selectedRestaurantId || '');
                                    const accent = selected ? m.colors.primary : m.colors.heading;
                                    const placeTypeLabel = (item.placeTypes || [])
                                        .map(formatPlaceType)
                                        .filter(Boolean)
                                        .slice(0, 2)
                                        .join(' • ');
                                    return (
                                        <Pressable
                                            key={itemId}
                                            accessibilityRole="button"
                                            accessibilityState={{selected}}
                                            onPress={() => setSelectedRestaurantId(itemId)}
                                            style={({pressed}) => [
                                                styles.restaurantItem,
                                                {
                                                    borderColor: selected ? m.colors.primary : m.colors.border,
                                                    backgroundColor: selected ? m.colors.chipBg : m.colors.surfaceAlt,
                                                    borderWidth: selected ? 1.6 : 1,
                                                    opacity: pressed ? 0.88 : 1,
                                                },
                                            ]}
                                        >
                                            <View style={styles.restaurantItemHeader}>
                                                <View
                                                    style={[
                                                        styles.restaurantAvatar,
                                                        {backgroundColor: selected ? m.colors.primary : m.colors.chipBg},
                                                    ]}
                                                >
                                                    <Text style={[styles.restaurantAvatarText, {color: selected ? m.colors.onPrimary : m.colors.chipText}]}>
                                                        {getInitials(item.name)}
                                                    </Text>
                                                </View>

                                                <View style={styles.restaurantHeaderBody}>
                                                    <Text style={[styles.restaurantName, {color: accent}]} numberOfLines={1}>
                                                        {item.name}
                                                    </Text>
                                                    {!!item.billingName && item.billingName !== item.name && (
                                                        <Text style={[styles.restaurantMeta, {color: m.colors.subText}]} numberOfLines={1}>
                                                            {item.billingName}
                                                        </Text>
                                                    )}
                                                </View>

                                                <Icon
                                                    name={selected ? 'check-circle' : 'circle-outline'}
                                                    size={22}
                                                    color={selected ? m.colors.primary : m.colors.muted}
                                                />
                                            </View>

                                            <View style={styles.restaurantBadgeRow}>
                                                {item.placesCount > 0 && (
                                                    <View style={[styles.restaurantBadge, {backgroundColor: m.colors.surface}]}>
                                                        <Icon name="store-outline" size={13} color={m.colors.subText} />
                                                        <Text style={[styles.restaurantBadgeText, {color: m.colors.subText}]}>
                                                            {t('mgmt_settings_places_count', {count: item.placesCount})}
                                                        </Text>
                                                    </View>
                                                )}
                                                {!!placeTypeLabel && (
                                                    <View style={[styles.restaurantBadge, {backgroundColor: m.colors.surface}]}>
                                                        <Icon name="tag-outline" size={13} color={m.colors.subText} />
                                                        <Text style={[styles.restaurantBadgeText, {color: m.colors.subText}]}>
                                                            {placeTypeLabel}
                                                        </Text>
                                                    </View>
                                                )}
                                                {item.placesCount > 0 && (
                                                    <View style={[styles.restaurantBadge, {backgroundColor: m.colors.surface}]}>
                                                        <Icon
                                                            name={item.isAnyOpen ? 'circle' : 'circle-outline'}
                                                            size={11}
                                                            color={item.isAnyOpen ? m.colors.success : m.colors.muted}
                                                        />
                                                        <Text style={[styles.restaurantBadgeText, {color: m.colors.subText}]}>
                                                            {item.isAnyOpen ? t('mgmt_settings_open_count', {count: item.openCount}) : t('mgmt_settings_closed')}
                                                        </Text>
                                                    </View>
                                                )}
                                            </View>

                                            {(!!item.vatNo || !!item.address || !!item.phone) && (
                                                <View style={styles.restaurantDetailBlock}>
                                                    {!!item.vatNo && (
                                                        <View style={styles.restaurantDetailRow}>
                                                            <Icon name="card-account-details-outline" size={14} color={m.colors.muted} />
                                                            <Text style={[styles.restaurantDetailText, {color: m.colors.subText}]} numberOfLines={1}>
                                                                {item.vatNo}
                                                            </Text>
                                                        </View>
                                                    )}
                                                    {!!item.address && (
                                                        <View style={styles.restaurantDetailRow}>
                                                            <Icon name="map-marker-outline" size={14} color={m.colors.muted} />
                                                            <Text style={[styles.restaurantDetailText, {color: m.colors.subText}]} numberOfLines={1}>
                                                                {item.address}
                                                            </Text>
                                                        </View>
                                                    )}
                                                    {!!item.phone && (
                                                        <View style={styles.restaurantDetailRow}>
                                                            <Icon name="phone-outline" size={14} color={m.colors.muted} />
                                                            <Text style={[styles.restaurantDetailText, {color: m.colors.subText}]} numberOfLines={1}>
                                                                {item.phone}
                                                            </Text>
                                                        </View>
                                                    )}
                                                </View>
                                            )}
                                        </Pressable>
                                    );
                                })}
                            </View>
                        )}

                        <View style={styles.partnerActionsRow}>
                            <Pressable
                                onPress={handleLoginAsRestaurant}
                                disabled={!isAdmin || !selectedRestaurant || switchingRestaurant}
                                accessibilityRole="button"
                                style={({pressed}) => [
                                    styles.primaryAction,
                                    {
                                        opacity: (!isAdmin || !selectedRestaurant || switchingRestaurant) ? 0.55 : (pressed ? 0.9 : 1),
                                        backgroundColor: m.colors.primary,
                                    },
                                ]}
                            >
                                <Text style={styles.primaryActionText} numberOfLines={1}>
                                    {switchingRestaurant
                                        ? t('mgmt_settings_switching')
                                        : t('mgmt_settings_login_as', {name: selectedRestaurant?.name || t('mgmt_settings_selected_restaurant')})}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={handleRestoreDefaultContext}
                                disabled={!isAdmin || switchingRestaurant}
                                accessibilityRole="button"
                                style={({pressed}) => [
                                    styles.secondaryAction,
                                    {
                                        opacity: (!isAdmin || switchingRestaurant) ? 0.6 : (pressed ? 0.85 : 1),
                                        borderColor: m.colors.border,
                                        backgroundColor: m.colors.surfaceAlt,
                                    },
                                ]}
                            >
                                <Text style={[styles.secondaryActionText, {color: m.colors.heading}]} numberOfLines={1}>{t('mgmt_settings_restore_own_partner')}</Text>
                            </Pressable>
                        </View>
                    </View>
                )}
                {navRows.map((item, index) => {
                    const access = canAccess(item.key);

                    return (
                        <SettingsRow
                            key={item.key}
                            icon={item.icon}
                            tone={item.tone}
                            title={item.title}
                            subtitle={item.subtitle}
                            disabled={!access.allowed}
                            lockedReason={access.reason}
                            onPress={item.onPress}
                            last={index === navRows.length - 1}
                        />
                    );
                })}
            </Card>

            {isSystemAdmin(currentUser, activePlace) && (
                <>
                    <SectionTitle title={t('mgmt_settings_developer_section', {defaultValue: 'Developer'})} icon="code-tags" tone="info" eyebrow />
                    <Card padded={false} style={styles.groupCard}>
                        <SettingsRow
                            icon="rocket-launch-outline"
                            tone="warning"
                            title={t('mgmt_deploy_menu_title', {defaultValue: 'CI/CD'})}
                            subtitle={t('mgmt_deploy_menu_subtitle', {defaultValue: 'Deployments, Docker & auto-deploy'})}
                            onPress={() => navigation.navigate('ManagementDeploymentsScreen')}
                        />
                        <SettingsRow
                            icon="pulse"
                            tone="info"
                            title={t('mgmt_system_status_menu_title', {defaultValue: 'Rendszerállapot'})}
                            subtitle={t('mgmt_system_status_menu_subtitle', {defaultValue: 'CPU, memória, PM2, naplók, adatbázis & cache'})}
                            onPress={() => navigation.navigate('ManagementSystemStatusScreen')}
                            last
                        />
                    </Card>
                </>
            )}
        </ManagementScreen>
    );
}

const styles = StyleSheet.create({
    profile: {borderRadius: 26, borderWidth: 1, padding: 16, marginBottom: 6},
    profileTop: {flexDirection: 'row', alignItems: 'center', gap: 14},
    avatarRing: {borderWidth: 2, borderRadius: 40, padding: 2},
    profileText: {flex: 1, minWidth: 0},
    profileEyebrow: {fontSize: 11.5, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.8},
    profileName: {fontSize: 21, fontWeight: '800', letterSpacing: -0.5, marginTop: 2},
    profileRole: {fontSize: 13.5, fontWeight: '600', marginTop: 3},
    profileActions: {flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start'},
    profileChips: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14},
    scopePill: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7, maxWidth: '100%'},
    scopePillText: {fontSize: 12.5, fontWeight: '700', flexShrink: 1},

    groupCard: {overflow: 'hidden'},
    row: {flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 14, paddingVertical: 13},
    rowText: {flex: 1, minWidth: 0},
    rowTitle: {fontSize: 15, fontWeight: '700'},
    rowSub: {fontSize: 12.5, fontWeight: '500', marginTop: 2, lineHeight: 17},

    cardHint: {fontSize: 13, fontWeight: '600', marginBottom: 12, lineHeight: 18},
    pillRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4},
    pill: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, borderRadius: 999, paddingHorizontal: 15, paddingVertical: 11},
    pillGrow: {flexGrow: 1, flexBasis: 0, minWidth: 96},
    pillStacked: {flexDirection: 'column', gap: 6, borderRadius: 22, paddingVertical: 14, paddingHorizontal: 8},
    pillText: {fontSize: 13.5, fontWeight: '800', flexShrink: 1},
    blockHead: {flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12},
    blockTitle: {flex: 1, fontSize: 15.5, fontWeight: '800'},
    landingGroupLabel: {fontSize: 12, fontWeight: '900', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 10, opacity: 0.85},
    landingSwitchBlock: {marginTop: 16, paddingTop: 14, borderTopWidth: 1, borderTopColor: 'rgba(148,163,184,0.24)'},
    moduleHint: {marginTop: 8, fontSize: 12.5, fontWeight: '600', lineHeight: 18},
    refreshBlock: {marginTop: 18, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(148,163,184,0.24)'},

    partnerBody: {paddingHorizontal: 14, paddingTop: 4, paddingBottom: 16, borderBottomWidth: StyleSheet.hairlineWidth},
    loaderRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12},
    loaderText: {marginLeft: 8, fontSize: 13.5, fontWeight: '600'},
    restaurantList: {gap: 10},
    restaurantItem: {borderRadius: 20, paddingHorizontal: 14, paddingVertical: 13},
    restaurantItemHeader: {flexDirection: 'row', alignItems: 'center'},
    restaurantAvatar: {width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center', marginRight: 12},
    restaurantAvatarText: {fontSize: 14.5, fontWeight: '900', letterSpacing: 0.4},
    restaurantHeaderBody: {flex: 1, paddingRight: 8},
    restaurantName: {fontSize: 15, fontWeight: '800'},
    restaurantMeta: {marginTop: 2, fontSize: 12.5, fontWeight: '600'},
    restaurantBadgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12},
    restaurantBadge: {flexDirection: 'row', alignItems: 'center', gap: 5, borderRadius: 999, paddingHorizontal: 9, paddingVertical: 5},
    restaurantBadgeText: {fontSize: 11.5, fontWeight: '800'},
    restaurantDetailBlock: {marginTop: 11, paddingTop: 11, borderTopWidth: 1, borderTopColor: 'rgba(148,163,184,0.22)', gap: 6},
    restaurantDetailRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
    restaurantDetailText: {flex: 1, fontSize: 12.5, fontWeight: '600'},
    listCounter: {fontSize: 12.5, fontWeight: '700'},
    emptyListText: {fontSize: 13, fontWeight: '600', fontStyle: 'italic'},
    partnerActionsRow: {marginTop: 14, gap: 8},
    primaryAction: {borderRadius: 999, minHeight: 48, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center'},
    primaryActionText: {color: '#FFFFFF', fontSize: 14.5, fontWeight: '800'},
    secondaryAction: {borderRadius: 999, borderWidth: 1, minHeight: 46, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center'},
    secondaryActionText: {fontSize: 14, fontWeight: '800'},
});
